import React from 'react';
import { 
  Instagram, Facebook, Linkedin,  
  MapPin, Phone, Mail, ArrowRight 
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  // SEO: Added descriptive data for social links
  const socialLinks = [
    { icon: Instagram, name: 'Instagram', url: '#' },
    { icon: Facebook, name: 'Facebook', url: '#' },
    { icon: Linkedin, name: 'LinkedIn', url: '#' },
    // { icon: Twitter, name: 'Twitter', url: '#' }
  ];

  return (
    <footer className="bg-neutral-950 text-gray-300 pt-20 pb-10 border-t border-amber-900/30 font-sans relative overflow-hidden">
      
      {/* Decorative Gold Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-amber-900/10 blur-[100px] pointer-events-none rounded-full"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* TOP SECTION: Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          {/* Column 1: Brand Info */}
          <div className="space-y-6">
            <div className="flex flex-col items-start">
               <span className="text-3xl text-amber-600 mb-1">♛</span>
               <h2 className="text-2xl font-serif font-bold text-white tracking-[0.2em] uppercase">
                 Luxuriya
               </h2>
               <span className="text-[0.6rem] tracking-[0.4em] text-gray-500 uppercase">
                 Homes
               </span>
            </div>
            <p className="text-sm text-gray-500 leading-relaxed pr-4">
              Crafting legacies in every corner. We build spaces that define elegance, royalty, and timeless comfort for generations.
            </p>
            
            {/* Social Icons - SEO Update: Added aria-labels and rel attributes */}
            <div className="flex space-x-4">
              {socialLinks.map((item, idx) => (
                <a 
                  key={idx} 
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visit Luxuriya Homes on ${item.name}`}
                  className="w-10 h-10 rounded-full border border-gray-800 flex items-center justify-center hover:bg-amber-600 hover:border-amber-600 hover:text-white transition-all duration-300"
                >
                  <item.icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Quick Links */}
         <div>
          <h3 className="text-white font-serif text-lg mb-6 tracking-wide">
            Quick Links
          </h3>

          <ul className="space-y-4 text-sm">
            {[
              { name: 'About Us', path: '/about' },
              { name: 'Services', path: '/services' },
              { name: 'Our Projects', path: '/projects' },
              // { name: 'Testimonials', path: '/testimonials' },
              { name: 'Contact Us', path: '/contact' },
            ].map((item) => (
              <li key={item.name}>
                <Link
                  to={item.path}
                  className="hover:text-amber-500 transition-colors duration-300 flex items-center gap-2 group"
                >
                  <span className="w-0 group-hover:w-2 h-[1px] bg-amber-500 transition-all duration-300"></span>
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

          {/* Column 3: Our Services */}
         <div>
          <h3 className="text-white font-serif text-lg mb-6 tracking-wide">
            Our Expertise
          </h3>

          <ul className="space-y-4 text-sm">
            {[
              { name: 'Luxury Builders', path: '/services/builders' },
              { name: 'Interior Designing', path: '/services/interior-design' },
              // { name: 'Architecture Planning', path: '/services/architecture' },
              { name: 'Modular Kitchens', path: '/gallery/kitchen' },
              { name: 'LuxuryOffice', path: '/gallery/luxury-office' },
            ].map((item) => (
              <li key={item.name}>
                <Link
                  to={item.path}
                  className="hover:text-amber-500 transition-colors duration-300 flex items-center gap-2 group"
                >
                  <span className="w-0 group-hover:w-2 h-[1px] bg-amber-500 transition-all duration-300"></span>
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

          {/* Column 4: Contact & Newsletter */}
          <div>
            <h3 className="text-white font-serif text-lg mb-6 tracking-wide">Get in Touch</h3>
            <address className="space-y-4 text-sm mb-8 not-italic">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-amber-600 mt-1 shrink-0" />
               <p>
  Ithum Tower, Sector 62<br />
  Noida, Uttar Pradesh, India
</p>

              </div>
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-amber-600 shrink-0" />
                <a href="tel:+917291832000" className="hover:text-amber-500 transition-colors">+91 7291832000</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-amber-600 shrink-0" />
                <a href="mailto:hello@luxuriyahomes.com" className="hover:text-amber-500 transition-colors">hello@luxuriyahomes.com</a>
              </div>
            </address>

            {/* Newsletter Input - SEO Update: Added Labels */}
            <form className="relative" onSubmit={(e) => e.preventDefault()}>
              <label htmlFor="newsletter-email" className="sr-only">Your Email Address</label>
              <input 
                id="newsletter-email"
                type="email" 
                placeholder="Your Email Address" 
                className="w-full bg-neutral-900 border-b border-gray-700 py-3 pl-2 pr-10 text-sm focus:outline-none focus:border-amber-600 transition-colors"
              />
              <button 
                type="submit"
                aria-label="Subscribe to newsletter"
                className="absolute right-0 top-1/2 -translate-y-1/2 text-amber-600 hover:text-white transition-colors"
              >
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>

        {/* BOTTOM BAR: Copyright */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs tracking-wider text-gray-500">
          <p>© 2025 Luxuriya Homes. All Rights Reserved.</p>
          <div className="flex space-x-8">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-condition" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;